import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Plus, 
  X, 
  Package, 
  Tag, 
  Globe, 
  BarChart3, 
  Settings, 
  ArrowLeft,
  Save,
  Trash2,
  Copy,
  Image as ImageIcon
} from 'lucide-react';
import api from '@/utils/api';
import { isAxiosError } from 'axios';
import { useCurrentStore } from '@/hooks/useCurrentStore';
import { getCurrencySymbol } from '@/utils/currency';
import { CreateCategoryDialog } from './CreateCategoryDialog';
import { ProductImageUploader, ProductImageAsset } from './ProductImageUploader';
import { ProductFormShell } from './ProductFormShell';
import { PageLoader } from '@/components/ui/full-page-loader';
import { cn } from '@/lib/utils';
import { useProductImageSuggestions } from '@/hooks/useProductImageSuggestions';

const editProductSchema = z
  .object({
  name: z.string().min(2, 'Product name must be at least 2 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
  basePrice: z.number().min(0, 'Price must be greater than 0'),
  compareAtPrice: z.number().optional().nullable(),
  category: z.string().min(1, 'Category is required'),
  sku: z.string().optional(),
  weight: z.number().optional().nullable(),
  dimensions: z.object({
    length: z.number().optional().nullable(),
    width: z.number().optional().nullable(),
    height: z.number().optional().nullable(),
    unit: z.enum(['cm', 'in']).default('cm'),
  }).optional(),
  lowStockThreshold: z.number().min(0).default(5),
  options: z.array(z.object({
    name: z.string().min(1, 'Option name is required'),
    required: z.boolean().default(false),
    values: z.array(z.object({
      name: z.string().min(1, 'Value name is required'),
      price_adjustment: z.number().default(0),
    })),
  })).default([]),
  seo: z.object({
    title: z.string().optional(),
    description: z.string().optional(),
    keywords: z.array(z.string()).default([]),
  }).default({}),
  status: z.enum(['draft', 'active', 'archived']).default('draft'),
  tags: z.array(z.string()).default([]),
  isDigital: z.boolean().default(false),
  shippingRequired: z.boolean().default(true),
  taxable: z.boolean().default(true),
  featured: z.boolean().default(false),
  vendor: z.string().optional(),
  wholesaleEnabled: z.boolean().default(false),
  wholesaleMinQuantity: z.number().min(2).optional().nullable(),
  wholesaleTierPrice: z.number().min(0).optional().nullable(),
})
  .superRefine((data, ctx) => {
    if (!data.wholesaleEnabled) return;
    if (data.wholesaleMinQuantity == null || data.wholesaleMinQuantity < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Minimum order quantity must be at least 2',
        path: ['wholesaleMinQuantity'],
      });
    }
    if (data.wholesaleTierPrice == null || data.wholesaleTierPrice <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter the total price for that minimum quantity',
        path: ['wholesaleTierPrice'],
      });
    }
  });

type EditProductFormData = z.infer<typeof editProductSchema>;

interface ProductImage {
  url: string;
  publicId?: string | null;
  alt?: string;
  isDefault?: boolean;
}

interface ProductVariant {
  _id?: string;
  options: Record<string, string>;
  sku?: string;
  price: number;
  inventory: number;
  wholesaleMinQuantity?: number | null;
  wholesaleTierPrice?: number | null;
}

interface ProductOption {
  name: string;
  values: Array<{ name: string; price_adjustment: number }>;
}

export function EditProductForm() {
  const navigate = useNavigate();
  const { productId } = useParams();
  const queryClient = useQueryClient();
  const { storeId } = useCurrentStore();
  const [productImages, setProductImages] = useState<ProductImageAsset[]>([]);
  const [newTag, setNewTag] = useState('');
  const [newKeyword, setNewKeyword] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [, setHasChanges] = useState(false);
  const [variantEdits, setVariantEdits] = useState<ProductVariant[]>([]);
  const [optionsDraft, setOptionsDraft] = useState<Array<{ name: string; values: string }>>([]);
  const [hasVariants, setHasVariants] = useState<boolean>(false);
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [step, setStep] = useState(0);

  const form = useForm<EditProductFormData>({
    resolver: zodResolver(editProductSchema),
    defaultValues: {
      name: '',
      description: '',
      basePrice: 0,
      compareAtPrice: null,
      category: '',
      sku: '',
      weight: null,
      dimensions: {
        length: null,
        width: null,
        height: null,
        unit: 'cm',
      },
      lowStockThreshold: 5,
      options: [],
      seo: {
        title: '',
        description: '',
        keywords: [],
      },
      status: 'draft',
      tags: [],
      isDigital: false,
      shippingRequired: true,
      taxable: true,
      featured: false,
      vendor: '',
      wholesaleEnabled: false,
      wholesaleMinQuantity: null,
      wholesaleTierPrice: null,
    },
  });

  // Watch for changes
  useEffect(() => {
    const subscription = form.watch(() => {
      setHasChanges(true);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Fetch existing product data
  const { data: product, isLoading } = useQuery({
    queryKey: ['product', productId],
    queryFn: async () => {
      if (!productId) return null;
      const { data } = await api.get(`/api/products/${productId}`);
      return data;
    },
    enabled: !!productId,
  });

  // Fetch categories
  const { data: categories = [] } = useQuery({
    queryKey: ['categories', storeId],
    queryFn: async () => {
      const { data } = await api.get('/api/categories?tree=true');
      return data;
    },
  });

  const { analyzing, handleUploadedFiles } = useProductImageSuggestions(
    form,
    categories,
  );

  // Populate form with existing product data
  useEffect(() => {
    if (product) {
      form.reset({
        name: product.name || '',
        description: product.description || '',
        basePrice: product.basePrice || 0,
        compareAtPrice: product.compareAtPrice || null,
        category: product.category?._id || product.category || '',
        sku: product.sku || '',
        weight: product.weight || null,
        dimensions: product.dimensions || {
          length: null,
          width: null,
          height: null,
          unit: 'cm',
        },
        lowStockThreshold: product.lowStockThreshold || 5,
        options: product.options || [],
        seo: product.seo || { title: '', description: '', keywords: [] },
        status: product.status || 'draft',
        tags: product.tags || [],
        isDigital: product.isDigital || false,
        shippingRequired: product.shippingRequired !== false,
        taxable: product.taxable !== false,
        featured: product.featured || false,
        vendor: product.vendor || '',
        wholesaleEnabled: Boolean(product.wholesaleEnabled),
        wholesaleMinQuantity: typeof product.wholesaleMinQuantity === 'number' ? product.wholesaleMinQuantity : null,
        wholesaleTierPrice: typeof product.wholesaleTierPrice === 'number' ? product.wholesaleTierPrice : null,
      });
      setHasChanges(false);
      const initialHasVariants = (product.options && product.options.length > 0) || ((product.variants || []).some((v: ProductVariant) => v.options && Object.keys(v.options).length > 0));
      setHasVariants(!!initialHasVariants);
      const initialVariants = (product.variants || []).map((v: ProductVariant) => ({
        _id: v._id,
        options: v.options instanceof Map ? Object.fromEntries(v.options) : (v.options || {}),
        sku: v.sku,
        price: v.price,
        inventory: v.inventory ?? 0,
        wholesaleMinQuantity: typeof v.wholesaleMinQuantity === 'number' ? v.wholesaleMinQuantity : null,
        wholesaleTierPrice: typeof v.wholesaleTierPrice === 'number' ? v.wholesaleTierPrice : null,
      }));
      setVariantEdits(initialHasVariants ? initialVariants : (initialVariants[0] ? [initialVariants[0]] : [{ options: {}, sku: product.sku, price: product.basePrice || 0, inventory: 0 }]));
      setOptionsDraft((product.options || []).map((o: ProductOption) => ({ name: o.name, values: (o.values || []).map((v) => v.name).join(',') })));
      setProductImages(
        Array.isArray(product.images)
          ? product.images.map((image: ProductImage) => ({
              url: image.url,
              publicId: image.publicId,
              alt: image.alt,
              isDefault: image.isDefault,
            }))
          : []
      );
    }
  }, [product, form]);

  // Ensure category is set once product loads if it's still empty
  useEffect(() => {
    if (product) {
      const current = form.getValues('category');
      const productCategoryId = (typeof product.category === 'object' && product.category?._id) ? String(product.category._id) : (product.category ? String(product.category) : '');
      if (!current && productCategoryId) {
        form.setValue('category', productCategoryId);
      }
    }
  }, [product, form]);

  const updateMutation = useMutation({
    mutationFn: async (data: EditProductFormData) => {
      const payload = {
        ...data,
        images: productImages,
        options: hasVariants ? optionsDraft.map(o => ({ name: o.name.trim(), required: true, values: o.values.split(',').map(v => ({ name: v.trim(), price_adjustment: 0 })).filter(v => v.name) })) : [],
        variants: hasVariants 
          ? variantEdits.map(v => ({
              _id: v._id,
              options: v.options,
              sku: v.sku,
              price: v.price,
              inventory: v.inventory,
              ...(data.wholesaleEnabled &&
              v.wholesaleMinQuantity != null &&
              v.wholesaleTierPrice != null
                ? {
                    wholesaleMinQuantity: v.wholesaleMinQuantity,
                    wholesaleTierPrice: v.wholesaleTierPrice,
                  }
                : {}),
            }))
          : [
              {
                _id: variantEdits[0]?._id,
                options: {},
                sku: data.sku,
                price: data.basePrice,
                inventory: variantEdits[0]?.inventory ?? 0,
              },
            ],
      };
      return api.patch(`/api/products/${productId}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product', productId] });
      toast.success('Product updated successfully');
      setHasChanges(false);
      navigate('/dashboard/products');
    },
    onError: (error: unknown) => {
      console.error('Product update error:', error);
      toast.error('Failed to update product');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      return api.delete(`/api/products/${productId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully');
      navigate('/dashboard/products');
    },
    onError: () => {
      toast.error('Failed to delete product');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const formData = form.getValues();
      const payload = {
        ...formData,
        name: `${formData.name} (Copy)`,
        sku: formData.sku ? `${formData.sku}-COPY` : undefined,
        status: 'draft',
      };
      return api.post('/api/products', payload);
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product duplicated successfully');
      navigate(`/dashboard/products/${response.data._id}`);
    },
    onError: (error: unknown) => {
      if (isAxiosError(error) && error.response?.status === 403) {
        const data = error.response?.data as { code?: string; message?: string };
        if (data?.code === 'PRODUCT_LIMIT') {
          toast.error(data.message || 'Product limit reached');
          return;
        }
      }
      toast.error('Failed to duplicate product');
    },
  });

  const onSubmit = (data: EditProductFormData) => {
    const safeData: EditProductFormData = {
      ...data,
      status: (data.status && ['draft', 'active', 'archived'].includes(data.status)) ? data.status : 'draft',
    };
    updateMutation.mutate(safeData);
  };

  const addTag = () => {
    if (newTag.trim()) {
      const currentTags = form.getValues('tags');
      if (!currentTags.includes(newTag.trim())) {
        form.setValue('tags', [...currentTags, newTag.trim()]);
      }
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    const currentTags = form.getValues('tags');
    form.setValue('tags', currentTags.filter(tag => tag !== tagToRemove));
  };

  const addKeyword = () => {
    if (newKeyword.trim()) {
      const currentKeywords = form.getValues('seo.keywords');
      if (!currentKeywords.includes(newKeyword.trim())) {
        form.setValue('seo.keywords', [...currentKeywords, newKeyword.trim()]);
      }
      setNewKeyword('');
    }
  };

  const removeKeyword = (keywordToRemove: string) => {
    const currentKeywords = form.getValues('seo.keywords');
    form.setValue('seo.keywords', currentKeywords.filter(keyword => keyword !== keywordToRemove));
  };

  // Flatten hierarchical categories for display
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const flattenCategories = (cats: any[]): Array<{ _id: string; name: string; level: number }> => {
    const result: Array<{ _id: string; name: string; level: number }> = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const flatten = (categories: any[], level = 0) => {
      categories.forEach(cat => {
        result.push({ _id: cat._id, name: cat.name, level });
        if (cat.children && cat.children.length > 0) {
          flatten(cat.children, level + 1);
        }
      });
    };
    flatten(cats);
    return result;
  };

  const flatCategories = flattenCategories(categories);

  // Ensure the product's current category is available in the list even if it's not in fetched categories
  const selectedCategoryOption = React.useMemo(() => {
    if (!product || !product.category) return null;
    if (typeof product.category === 'object') {
      return { _id: product.category._id as string, name: product.category.name as string, level: 0 };
    }
    // If only id is present without name, fallback without name
    return { _id: String(product.category), name: 'Current Category', level: 0 };
  }, [product]);

  const flatCategoriesWithSelected = React.useMemo(() => {
    const flat = flatCategories;
    if (selectedCategoryOption && !flat.some(c => c._id === selectedCategoryOption._id)) {
      return [selectedCategoryOption, ...flat];
    }
    return flat;
  }, [flatCategories, selectedCategoryOption]);

  const totalVariantInventory = variantEdits.reduce(
    (sum, variant) => sum + (variant.inventory ?? 0),
    0
  );

  const handleCategoryCreated = (categoryId: string) => {
    form.setValue('category', categoryId);
  };

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    deleteMutation.mutate();
    setShowDeleteDialog(false);
  };

  if (isLoading) {
    return <PageLoader text="Loading product" />;
  }

  if (!product) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900 mb-2">Product not found</h3>
        <Button onClick={() => navigate('/dashboard/products')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Products
        </Button>
      </div>
    );
  }

  return (
    <ProductFormShell
      title={product.name || 'Edit product'}
      step={step}
      onStepChange={setStep}
      onBack={() => navigate('/dashboard/products')}
      onSave={() => form.handleSubmit(onSubmit)()}
      saveLabel="Save changes"
      saving={updateMutation.isPending}
      canContinue={async () => {
        if (step !== 0) return true;
        return form.trigger(['name', 'description', 'category', 'basePrice']);
      }}
      badges={
        <>
          {product.status === 'active' && (
            <Badge variant="secondary" className="rounded-full bg-green-50 text-green-700">
              Published
            </Badge>
          )}
          {product.featured && (
            <Badge variant="secondary" className="rounded-full bg-yellow-50 text-yellow-700">
              Featured
            </Badge>
          )}
        </>
      }
      actions={
        <>
          <Button
            variant="outline"
            onClick={() => duplicateMutation.mutate()}
            disabled={duplicateMutation.isPending}
            className="h-10 rounded-full px-4 text-sm font-semibold shadow-none"
            size="sm"
          >
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </Button>
          <Button
            variant="outline"
            onClick={handleDelete}
            className="h-10 rounded-full px-4 text-sm font-semibold text-red-600 shadow-none hover:text-red-700"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </Button>
        </>
      }
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div hidden={step !== 0} className={cn(step === 0 && "space-y-8")}>
          <section className="space-y-8">
              <div className="space-y-2">
                <FormLabel>Photos</FormLabel>
                <ProductImageUploader
                  images={productImages}
                  onChange={(items) => {
                    setProductImages(items);
                    setHasChanges(true);
                  }}
                  maxImages={8}
                  analyzing={analyzing}
                  onFilesUploaded={handleUploadedFiles}
                />
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Product Name *</FormLabel>
                        <FormControl>
                          <Input placeholder="Enter product name" {...field} className="h-10 border-gray-300 text-sm" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description *</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe your product..."
                            rows={6}
                            {...field}
                            className="min-h-[140px] border-[#EBEBEB] text-sm"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category *</FormLabel>
                        <div className="flex gap-2">
                          <Select onValueChange={field.onChange} value={field.value ?? ''} key={field.value ?? 'empty'} className="flex-1">
                            <FormControl>
                              <SelectTrigger className="h-10 border-gray-300 text-sm w-full">
                                <SelectValue placeholder="Select a category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {flatCategoriesWithSelected.length === 0 ? (
                                <SelectItem value="__none__" disabled>
                                  No categories available
                                </SelectItem>
                              ) : (
                                flatCategoriesWithSelected.map((category) => (
                                  <SelectItem key={category._id} value={category._id}>
                                    {'— '.repeat(category.level)}{category.name}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            onClick={() => setShowCreateCategory(true)}
                            className="shrink-0 h-9 text-sm"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            New
                          </Button>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value ?? 'draft'} key={field.value ?? 'draft'}>
                          <FormControl>
                            <SelectTrigger className="h-10 border-gray-300 text-sm w-full">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="active">Active</SelectItem>
                            <SelectItem value="archived">Archived</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="basePrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price *</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              {...field}
                              onChange={e => { field.onChange(parseFloat(e.target.value) || 0); setHasChanges(true); }}
                              className="h-10 border-gray-300 text-sm"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="compareAtPrice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Compare at Price</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              {...field}
                              value={field.value || ''}
                              onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                              className="h-10 border-gray-300 text-sm"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4 space-y-4 bg-muted/30">
                    <FormField
                      control={form.control}
                      name="wholesaleEnabled"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between">
                          <div>
                            <FormLabel>Wholesale / bulk pricing</FormLabel>
                            <FormDescription>
                              Total price for the minimum quantity (not per unit). Scales for larger orders.
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={(v) => {
                                field.onChange(v);
                                setHasChanges(true);
                              }}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    {form.watch('wholesaleEnabled') && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="wholesaleMinQuantity"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Minimum order quantity</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  min={2}
                                  step={1}
                                  placeholder="e.g. 12"
                                  className="h-10 border-gray-300 text-sm"
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    field.onChange(e.target.value ? parseInt(e.target.value, 10) : null);
                                    setHasChanges(true);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name="wholesaleTierPrice"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Total price for that quantity</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.01"
                                  min={0}
                                  placeholder="0.00"
                                  className="h-10 border-gray-300 text-sm"
                                  value={field.value ?? ''}
                                  onChange={(e) => {
                                    field.onChange(e.target.value ? parseFloat(e.target.value) : null);
                                    setHasChanges(true);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}
                  </div>

                  <FormField
                    control={form.control}
                    name="sku"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>SKU</FormLabel>
                        <FormControl>
                          <Input placeholder="Product SKU" {...field} className="h-10 border-gray-300 text-sm" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div>
                    <FormLabel className="text-sm">Available Stock</FormLabel>
                    {hasVariants ? (
                      <>
                        <Input
                          value={totalVariantInventory}
                          readOnly
                          className="h-10 border-gray-300 text-sm bg-muted"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Managed across variants. Update individual quantities below.
                        </p>
                      </>
                    ) : (
                      <>
                        <Input
                          type="number"
                          min={0}
                          value={variantEdits[0]?.inventory ?? 0}
                          onChange={(e) => {
                            const arr = [...variantEdits];
                            arr[0] = {
                              ...(arr[0] || {
                                options: {},
                                sku: form.getValues('sku'),
                                price: form.getValues('basePrice') || 0,
                              }),
                              inventory: Math.max(0, parseInt(e.target.value || '0')),
                            };
                            setVariantEdits(arr);
                            setHasChanges(true);
                          }}
                          className="h-10 border-gray-300 text-sm"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Customers cannot purchase more than this quantity.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
          </section>
          </div>

          <div hidden={step !== 2} className={cn(step === 2 && "space-y-8")}>
          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                Product Attributes
            </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="vendor"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Vendor</FormLabel>
                      <FormControl>
                        <Input placeholder="Vendor name" {...field} className="!h-10 border-gray-300 text-sm" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="weight"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Weight (kg)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ''}
                          className="!h-10 border-gray-300 text-sm"
                          onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="dimensions.length"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Length</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ''}
                          onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          className="!h-10 border-gray-300 text-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dimensions.width"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Width</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ''}
                          onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          className="!h-10 border-gray-300 text-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dimensions.height"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Height</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          {...field}
                          value={field.value || ''}
                          onChange={e => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                          className="!h-10 border-gray-300 text-sm"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="dimensions.unit"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Unit</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className="!h-10 border-gray-300 text-sm w-full">
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="cm">cm</SelectItem>
                          <SelectItem value="in">inches</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="space-y-2">
                <FormLabel>Tags</FormLabel>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.watch('tags').map((tag, index) => (
                    <Badge key={index} variant="secondary" className="flex items-center gap-1">
                      {tag}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => removeTag(tag)}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add tag"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="!h-10 border-gray-300 text-sm"
                  />
                  <Button type="button" onClick={addTag} size="sm" className="h-10 text-sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
          </section>
          </div>

          <div hidden={step !== 1} className={cn(step === 1 && "space-y-8")}>
          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                Variant Settings
            </h2>
              <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                <div className="space-y-0.5">
                  <FormLabel className="text-sm">This product has variants</FormLabel>
                  <FormDescription className="text-xs">
                    Enable variants to manage options and per-variant inventory
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={hasVariants}
                    onCheckedChange={(checked) => {
                      setHasVariants(checked);
                      setHasChanges(true);
                      if (!checked) {
                        // collapse to single default variant
                        setOptionsDraft([]);
                        setVariantEdits(prev => [prev[0] ? { ...prev[0], options: {} } : { options: {}, sku: form.getValues('sku'), price: form.getValues('basePrice') || 0, inventory: 0 }]);
                      }
                    }}
                  />
                </FormControl>
              </FormItem>

              {hasVariants ? (
                <>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Define option groups (e.g., Size, Color)</Label>
                      <Button type="button" variant="outline" size="sm" onClick={() => setOptionsDraft([...optionsDraft, { name: '', values: '' }])}>Add Option</Button>
                    </div>
                    <div className="space-y-3">
                      {optionsDraft.map((o, idx) => (
                        <div key={idx} className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Input placeholder="Option name" value={o.name} onChange={e => { const arr = [...optionsDraft]; arr[idx] = { ...arr[idx], name: e.target.value }; setOptionsDraft(arr); setHasChanges(true); }} className="!h-10 border-gray-300 text-sm" />
                          <Input placeholder="Comma separated values" value={o.values} onChange={e => { const arr = [...optionsDraft]; arr[idx] = { ...arr[idx], values: e.target.value }; setOptionsDraft(arr); setHasChanges(true); }} />
                        </div>
                      ))}
                      {optionsDraft.length === 0 && (
                        <p className="text-xs text-muted-foreground">No options defined.</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium">Variants & Stock</Label>
                    {variantEdits.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No variants. Add options above and save, or manage variants separately.</p>
                    ) : (
                      <div className="space-y-2">
                        {variantEdits.map((v, i) => (
                          <div key={v._id || i} className="border rounded-lg p-3 space-y-2">
                            <div className="grid min-w-0 grid-cols-1 gap-3 overflow-x-auto md:grid-cols-5">
                              <div className="text-sm">
                                {Object.entries(v.options || {}).map(([k, val]) => (
                                  <div key={k}><span className="text-muted-foreground">{k}:</span> {val as string}</div>
                                ))}
                              </div>
                              <Input placeholder="SKU" value={v.sku || ''} onChange={e => { const arr = [...variantEdits]; arr[i] = { ...arr[i], sku: e.target.value }; setVariantEdits(arr); setHasChanges(true); }} />
                              <Input type="number" step="0.01" placeholder="Price" value={v.price} onChange={e => { const arr = [...variantEdits]; arr[i] = { ...arr[i], price: parseFloat(e.target.value) || 0 }; setVariantEdits(arr); setHasChanges(true); }} />
                              <Input type="number" min={0} placeholder="Inventory" value={v.inventory} onChange={e => { const arr = [...variantEdits]; arr[i] = { ...arr[i], inventory: Math.max(0, parseInt(e.target.value || '0')) }; setVariantEdits(arr); setHasChanges(true); }} />
                              <div className="self-center text-xs text-muted-foreground">{getCurrencySymbol()}{v.price}</div>
                            </div>
                            {form.watch('wholesaleEnabled') && (
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1 border-t">
                                <Input
                                  type="number"
                                  min={2}
                                  step={1}
                                  placeholder="Wholesale MOQ override (optional)"
                                  value={v.wholesaleMinQuantity ?? ''}
                                  onChange={e => {
                                    const arr = [...variantEdits];
                                    arr[i] = { ...arr[i], wholesaleMinQuantity: e.target.value ? parseInt(e.target.value, 10) : null };
                                    setVariantEdits(arr);
                                    setHasChanges(true);
                                  }}
                                  className="h-9 border-gray-300 text-xs"
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  min={0}
                                  placeholder="Wholesale tier total override (optional)"
                                  value={v.wholesaleTierPrice ?? ''}
                                  onChange={e => {
                                    const arr = [...variantEdits];
                                    arr[i] = { ...arr[i], wholesaleTierPrice: e.target.value ? parseFloat(e.target.value) : null };
                                    setVariantEdits(arr);
                                    setHasChanges(true);
                                  }}
                                  className="h-9 border-gray-300 text-xs"
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <FormLabel className="text-sm">Default Variant SKU</FormLabel>
                    <Input placeholder="SKU" value={form.watch('sku') || ''} onChange={e => { form.setValue('sku', e.target.value); setHasChanges(true); }} className="!h-10 border-gray-300 text-sm" />
                  </div>
                  <div>
                    <FormLabel className="text-sm">Default Price</FormLabel>
                    <Input type="number" step="0.01" value={form.watch('basePrice') || 0} onChange={e => { form.setValue('basePrice', parseFloat(e.target.value) || 0); setHasChanges(true); }} className="h-10 border-gray-300 text-sm" />
                  </div>
                </div>
              )}
          </section>
          </div>

          <div hidden={step !== 2} className={cn(step === 2 && "space-y-8")}>
          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                SEO Settings
            </h2>
              <FormField
                control={form.control}
                name="seo.title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SEO Title</FormLabel>
                    <FormControl>
                      <Input placeholder="SEO title for search engines" {...field} className="h-10 border-gray-300 text-sm" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="seo.description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>SEO Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Brief description for search engines"
                        rows={3}
                        {...field}
                        className="h-10 border-gray-300 text-sm"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-2">
                <FormLabel>SEO Keywords</FormLabel>
                <div className="flex flex-wrap gap-2 mb-2">
                  {form.watch('seo.keywords').map((keyword, index) => (
                    <Badge key={index} variant="outline" className="flex items-center gap-1">
                      {keyword}
                      <X
                        className="w-3 h-3 cursor-pointer"
                        onClick={() => removeKeyword(keyword)}
                      />
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add keyword"
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    className="h-10 border-gray-300 text-sm"
                  />
                  <Button type="button" onClick={addKeyword} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
          </section>

          <section className="space-y-5">
            <h2 className="font-heading text-lg font-semibold tracking-tight text-primary">
                Product Settings
            </h2>
              <FormField
                control={form.control}
                name="featured"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">Featured Product</FormLabel>
                      <FormDescription className="text-xs">
                        Show this product in featured sections
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isDigital"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">Digital Product</FormLabel>
                      <FormDescription className="text-xs">
                        This product is delivered digitally
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="shippingRequired"
                render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">Shipping Required</FormLabel>
                      <FormDescription className="text-xs">
                    
                        This product requires shipping
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="taxable"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border border-gray-300 p-2">
                    <div className="space-y-0.5">
                      <FormLabel className="text-sm">Taxable</FormLabel>
                      <FormDescription className="text-xs">
                        Charge tax on this product
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lowStockThreshold"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm">Low Stock Threshold</FormLabel>
                   
                    <FormControl>
                      <Input
                        type="number"
                        min={0}
                        placeholder="5"
                        {...field}
                        onChange={e => field.onChange(parseInt(e.target.value) || 0)}
                        className="!h-10 border-gray-300 text-sm"
                      />
                    </FormControl>
                    <FormDescription>
                      Get notified when stock falls below this number
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
          </section>
          </div>
        </form>
      </Form>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Product</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{product.name}"? This action cannot be undone.
              All product data, including images and variants, will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete Product
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Category Dialog */}
      <CreateCategoryDialog
        isOpen={showCreateCategory}
        onClose={() => setShowCreateCategory(false)}
        onCategoryCreated={handleCategoryCreated}
        existingCategories={flatCategoriesWithSelected}
      />
    </ProductFormShell>
  );
}
